import React, { useMemo } from "react";

import DownloadIcon from "@uui/Download01";
import { compact, isEmpty } from "lodash/fp";
import { useParams } from "react-router-dom";

import styled from "@mui/material/styles/styled";

import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import { VerticalDivider } from "@app/analytics/components/reports/styles";
import useMeetingReport from "@app/analytics/MeetingReport/hooks/useMeetingReport";
import useMeetingReportSessionNotes from "@app/analytics/MeetingReport/hooks/useMeetingReportSessionNotes";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscript from "@app/analytics/MeetingReport/hooks/useTranscript";
import useVideoRecording from "@app/analytics/MeetingReport/hooks/useVideoRecording";
import * as Events from "@app/analytics/tracking/events";
import useApps from "@app/common/hooks/useApps";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import ROUTES from "@app/common/routes";
import { VideoRecording } from "@app/common/types";
import Config from "@app/config";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Link from "@rui/Link";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";

import * as DownloadUtils from "./utils/downloadUtils";
import {
  generateTextFileFromTranscript,
  generateTextFileFromSummary,
} from "./utils/transcriptUtils";

const DownloadLink = styled(Link)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.shades.neutral[800],
  padding: "8px",
  display: "block",
  width: "100%",
}));

const DownloadMenuItem = styled(MenuItem)({
  padding: 0,
});

const DownloadMenu = ({
  title,
  disabled = false,
}: {
  title: string;
  disabled?: boolean;
}) => {
  const { client } = useApps();
  const { sessionId } = useParams();
  const { canAccessTranscript, canAccessPlayback } = useFeatureAccess();
  const { anchorElement, open, onClose, onClick } = useAnchorElement();

  const {
    data: { highlightReelUrl, isRestrictedReport, isNotetakerReport },
  } = useSessionMetadata({ enabled: !disabled });

  const cannotAccessArtifacts =
    isRestrictedReport || (!canAccessTranscript && !canAccessPlayback);
  const queriesEnabled = !isRestrictedReport && !cannotAccessArtifacts;

  const { meetingStartTime } = useMeetingReport({
    enabled: queriesEnabled,
  });

  const { videoRecording, hasVideoRecording, trailerUrl } = useVideoRecording({
    enabled: queriesEnabled && canAccessPlayback,
  });

  const {
    rawTurns,
    summary,
    isEmpty: transcriptAndSummaryIsEmpty,
  } = useTranscript({ enabled: queriesEnabled });
  const { notes } = useMeetingReportSessionNotes({ enabled: queriesEnabled });

  const transcriptTxtFile = useMemo(
    () =>
      generateTextFileFromTranscript(rawTurns, {
        title,
        startTime: meetingStartTime,
      }),
    [rawTurns, title, meetingStartTime?.toISO()]
  );

  const summaryTxtFile = useMemo(
    () =>
      generateTextFileFromSummary(summary, notes, {
        title,
        startTime: meetingStartTime,
        isNotetakerReport,
      }),
    [summary, title, notes, meetingStartTime?.toISO(), isNotetakerReport]
  );

  const canDownloadTranscript =
    canAccessTranscript && !transcriptAndSummaryIsEmpty && !isNotetakerReport;
  const canDownloadSummary =
    canAccessTranscript && (!transcriptAndSummaryIsEmpty || !isEmpty(notes));
  const canDownloadTrailer = canAccessPlayback && trailerUrl != null;
  const canDownloadHighlightReel =
    canAccessPlayback && highlightReelUrl != null;
  const canDownloadVideo = canAccessPlayback && hasVideoRecording;

  const cannotDownloadAnything = isEmpty(
    compact([
      canDownloadTranscript,
      canDownloadSummary,
      canDownloadTrailer,
      canDownloadHighlightReel,
      canDownloadVideo,
    ])
  );

  if (cannotDownloadAnything) return null;

  const inZoomClient = client === "zoom";

  return (
    <>
      <HeaderButton
        className="meeting-report__header__downloads"
        startIcon={<DownloadIcon />}
        onClick={onClick}
        disabled={disabled}
      >
        Download
      </HeaderButton>
      <VerticalDivider size="md" />
      <Menu open={open} anchorEl={anchorElement} onClose={onClose}>
        {canDownloadSummary && (
          <DownloadMenuItem>
            <DownloadLink
              href={
                inZoomClient
                  ? `${ROUTES.GO.DOWNLOAD_SUMMARY}?sessionId=${sessionId}`
                  : `data:text/plain,${summaryTxtFile}`
              }
              download={
                inZoomClient
                  ? undefined
                  : DownloadUtils.getDownloadTitle(title, "Summary")
              }
              onClick={() => {
                Events.downloadSummary();
                onClose();
              }}
            >
              Meeting summary (.txt)
            </DownloadLink>
          </DownloadMenuItem>
        )}
        {canDownloadTranscript && (
          <DownloadMenuItem>
            <DownloadLink
              href={
                inZoomClient
                  ? `${ROUTES.GO.DOWNLOAD_TRANSCRIPT}?sessionId=${sessionId}`
                  : `data:text/plain,${transcriptTxtFile}`
              }
              download={
                inZoomClient
                  ? undefined
                  : DownloadUtils.getDownloadTitle(title, "Transcript")
              }
              onClick={() => {
                Events.downloadTranscript();
                onClose();
              }}
            >
              Meeting transcript (.txt)
            </DownloadLink>
          </DownloadMenuItem>
        )}
        {canDownloadTrailer && (
          <DownloadMenuItem>
            <DownloadLink
              href={DownloadUtils.getVideoDownloadUrl(
                trailerUrl,
                DownloadUtils.getDownloadTitle(title, "Trailer")
              )}
              download={DownloadUtils.getDownloadTitle(title, "Trailer")}
              onClick={() => {
                Events.downloadTrailer();
                onClose();
              }}
            >
              Meeting trailer (.mp4)
            </DownloadLink>
          </DownloadMenuItem>
        )}
        {canDownloadHighlightReel && (
          <DownloadMenuItem>
            <DownloadLink
              href={DownloadUtils.getVideoDownloadUrl(
                highlightReelUrl,
                DownloadUtils.getDownloadTitle(title, "Highlights")
              )}
              download={DownloadUtils.getDownloadTitle(title, "Highlights")}
              onClick={() => {
                Events.downloadHighlights();
                onClose();
              }}
            >
              Meeting highlights (.mp4)
            </DownloadLink>
          </DownloadMenuItem>
        )}
        {canDownloadVideo && (
          <DownloadMenuItem>
            <DownloadLink
              href={DownloadUtils.getVideoRecordingDownloadUrl(
                videoRecording as VideoRecording,
                {
                  filename: DownloadUtils.getDownloadTitle(title, "Recording"),
                  recordingsUrl: Config.RECORDINGS_URL,
                }
              )}
              download={DownloadUtils.getDownloadTitle(title, "Recording")}
              onClick={() => {
                Events.downloadRecording();
                onClose();
              }}
            >
              Meeting video (.mp4)
            </DownloadLink>
          </DownloadMenuItem>
        )}
      </Menu>
    </>
  );
};

export default DownloadMenu;
